import type { Expression } from "../types";
import cast from "./art/cast.json";
import parts from "./art/parts.json";
import palette from "./art/palette.json";
import { generateSpec, type PortraitSpec } from "./spec";

/**
 * Layered portrait art (22_art_brief.md), imported by scripts/import-art.mjs. Named characters use
 * their drawn portraits; everyone else is assembled from the parts kit, seeded from their id so
 * they always look the same. Only the brows, eyes, mouth and under-eye layers change with expression.
 */

export const ART_SIZE = 240;
const CAST = cast as Record<string, string>;
const PARTS = parts as Record<string, string>;

export function hasCastArt(portraitId: string | null | undefined): boolean {
  return !!portraitId && portraitId in CAST;
}

const HAIR_STYLE: Record<PortraitSpec["hairStyle"], string> = {
  short: "short", sidePart: "side-parting", receding: "receding", cropped: "cropped", curlyShort: "curly-short",
  bob: "bob", long: "long", bun: "bun", pixie: "pixie", shoulder: "shoulder-length", afro: "afro", bald: "bald",
  locs: "locs", hijab: "hijab",
};
const HAIR_COLOUR: Record<PortraitSpec["hairColour"], string> = {
  black: "black", darkBrown: "dark-brown", brown: "brown", auburn: "auburn", blonde: "blonde",
  saltPepper: "salt-and-pepper", grey: "grey", silver: "silver",
};
const CLOTHING: Record<PortraitSpec["clothing"], string> = {
  suitTie: "suit-tie", suitOpen: "open-suit", blazerBlouse: "blazer-blouse", cardigan: "cardigan", shirtTie: "shirt-tie", knit: "knit",
};
const GLASSES: Record<PortraitSpec["glasses"], string> = { none: "none", round: "round", rect: "rectangular" };

/** The spec's skin index (0–5) spread across the kit's seven palettes. */
const skinPalette = (i: number) => palette.skins[Math.min(palette.skins.length - 1, Math.round((i * (palette.skins.length - 1)) / 5))]!;

/** Darken a #rrggbb colour by a factor (0–1). */
function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(v * f).toString(16).padStart(2, "0"));
  return `#${c.join("")}`;
}

/** Assemble a generated person from the parts kit, colours filled in. */
export function assembleGenerated(spec: PortraitSpec): string {
  const hairStyle = HAIR_STYLE[spec.hairStyle];
  const keys = [
    `hair-back/${hairStyle}`, "neck/standard", `clothing/${CLOTHING[spec.clothing]}`, "ears/standard",
    `head/${spec.faceShape}`, `age-lines/${spec.age}`, "under-eye/tired",
    ...["brows", "eyes", "mouth"].flatMap((p) => ["neutral", "pleased", "concerned", "tired", "irritated", "upset", "guarded"].map((e) => `${p}/${e}`)),
    `facial-hair/${spec.facialHair}`, `glasses/${GLASSES[spec.glasses]}`, `hair-front/${hairStyle}`,
  ];
  // A hijab is fabric, not hair: colour it from the outfit's accent rather than the hair colour.
  const hair = spec.hairStyle === "hijab"
    ? { hair: spec.accentColour, "hair-shade": shade(spec.accentColour, 0.75) }
    : (palette.hair as Record<string, { hair: string; "hair-shade": string }>)[HAIR_COLOUR[spec.hairColour]]!;
  const colours: Record<string, string> = {
    ...skinPalette(spec.skin), ...hair,
    "fill-base": spec.clothingColour, "fill-inner": spec.innerColour, "fill-accent": spec.accentColour,
  };
  return '<g data-layer="backdrop"></g>' + keys.map((k) => PARTS[k] ?? "").join("")
    .replace(/var\(--([a-z-]+)\)/g, (_, token: string) => colours[token] ?? "#808080");
}

/**
 * Inner SVG markup for one portrait instance: the chosen expression's layers only, the under-eye
 * shading for tired (or for a neutral/pleased face whose strain the player can see), and gradient
 * ids made unique with `uid` so two copies of a face on one page don't share them.
 */
export function portraitMarkup(opts: {
  portraitId: string | null | undefined; seed: string; grade?: string | undefined;
  expression: Expression; strain?: boolean | undefined; uid: string;
}): string {
  const { portraitId, seed, grade, expression, strain, uid } = opts;
  const base = portraitId && CAST[portraitId] ? CAST[portraitId]! : assembleGenerated(generateSpec(seed || portraitId || "anonymous", grade));
  const shading = expression === "tired" || (!!strain && (expression === "neutral" || expression === "pleased"));
  let out = base
    // Keep only this expression's brows, eyes and mouth.
    .replace(/<g data-layer="(brows|eyes|mouth)-([a-z]+)"[^>]*>[\s\S]*?<\/g>/g, (m, part: string, e: string) =>
      e === expression ? `<g data-layer="${part}-${e}">${m.slice(m.indexOf(">") + 1)}` : "")
    .replace(/<g data-layer="under-eye"[^>]*>/, shading ? '<g data-layer="under-eye">' : '<g data-layer="under-eye" style="display:none">');
  const ids = [...out.matchAll(/<(?:linear|radial)Gradient[^>]* id="([^"]+)"/g)].map((m) => m[1]!);
  for (const id of ids) out = out.split(`id="${id}"`).join(`id="${uid}-${id}"`).split(`url(#${id})`).join(`url(#${uid}-${id})`);
  return out;
}
